import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import type { IntegrationConsent } from "@/shared/integrations/integration.types";
import { setAnalyticsConsent } from "@/shared/integrations/integration-runtime";

const CONSENT_STORAGE_KEY = "pw-integration-consent:v1";

interface ConsentContextValue {
  analytics: IntegrationConsent;
  grantAnalytics: () => void;
  denyAnalytics: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function readAnalyticsConsent(): IntegrationConsent {
  try {
    return parseAnalyticsConsent(localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return "unknown";
  }
}

export function parseAnalyticsConsent(value: string | null): IntegrationConsent {
  return value === "granted" || value === "denied" ? value : "unknown";
}

function persistAnalyticsConsent(value: Exclude<IntegrationConsent, "unknown">): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Storage can be unavailable. The current-page decision still applies.
  }
}

export function IntegrationConsentProvider({ children }: { children: ReactNode }) {
  const [analytics, setAnalytics] = useState<IntegrationConsent>(readAnalyticsConsent);

  useEffect(() => {
    setAnalyticsConsent(analytics);
  }, [analytics]);

  function choose(next: Exclude<IntegrationConsent, "unknown">): void {
    persistAnalyticsConsent(next);
    setAnalyticsConsent(next);
    setAnalytics(next);
  }

  return (
    <ConsentContext.Provider
      value={{
        analytics,
        grantAnalytics: () => choose("granted"),
        denyAnalytics: () => choose("denied"),
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export function useIntegrationConsent(): ConsentContextValue {
  const value = useContext(ConsentContext);
  if (!value) {
    throw new Error("useIntegrationConsent must be used within <IntegrationConsentProvider>");
  }
  return value;
}
